import { useEffect, useRef, useState, type ReactNode } from "react";

interface BackgroundVideoProps {
  src: string;
  poster: string;
  alt?: string;
  overlayClassName?: string;
  children?: ReactNode;
}

/**
 * Vídeo de fundo full-bleed com autoplay, mute, loop e playsInline.
 * - Exibe o poster como fallback enquanto o vídeo carrega ou quando ainda não existe.
 * - Aceita um overlay (gradiente) e filhos adicionais (brilhos, partículas, etc).
 */
export default function BackgroundVideo({
  src,
  poster,
  alt = "",
  overlayClassName = "",
  children,
}: BackgroundVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.play().catch(() => {
        /* vídeo ainda não disponível — mantém o poster */
      });
    };

    tryPlay();
    video.addEventListener("canplay", tryPlay);
    return () => video.removeEventListener("canplay", tryPlay);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={poster}
        alt={alt}
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        onPlaying={() => setReady(true)}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
          ready ? "opacity-100" : "opacity-0"
        }`}
      />
      {overlayClassName ? (
        <div className={`absolute inset-0 ${overlayClassName}`} />
      ) : null}
      {children}
    </div>
  );
}